/**
 * İÇERİK PAKETİ (Content Pack)
 *
 * Kullanıcının kendi yarattığı sınıf, ırk, arka plan, eşya ve büyüleri
 * taşıyan JSON belgesidir. Neden ayrı bir kavram:
 *
 *  1. **Paylaşılabilir** — 4. adımda (Cloudflare + davet) arkadaşlar aynı
 *     özel sınıfı oynayabilsin diye pakete ihtiyaç var. JSON en taşınabilir
 *     biçim.
 *  2. **Sürümlenebilir** — paket `version` taşır; kampanya bir pakete pin
 *     olabilir (ileride).
 *  3. **Motoru değiştirmez** — kural motoru içeriği `ContentIndex` üzerinden
 *     okur. Özel sınıf da tam olarak bir `ClassEntry`'dir; motor onu
 *     "özel" olarak bile bilmez. Bu yüzden bu dosya saf veri + doğrulamadır.
 *
 * BU DOSYA BAĞIMSIZDIR: I/O, ağ, tarayıcı API'si yok.
 */

import {
  ABILITY_KEYS,
  type AbilityKey,
  type BackgroundEntry,
  type ClassEntry,
  type ClassLevelEntry,
  type ContentIndex,
  type FeatureEntry,
  type ItemEntry,
  type PathEntry,
  type RaceEntry,
  SKILLS,
  type SpellcastingSpec,
  type SpellEntry,
} from './types'

export interface ContentPack {
  id: string
  name: string
  /** semver benzeri: "1.0.0" */
  version: string
  author: string
  /** ms zaman damgası */
  createdAt: number
  description: string
  classes: ClassEntry[]
  races: RaceEntry[]
  backgrounds: BackgroundEntry[]
  items: ItemEntry[]
  spells: SpellEntry[]
  /**
   * Özel yollar. Bir karakter istediği kadar yol alabilir; her yol kendi
   * seviyeli özelliklerini katman olarak ekler.
   */
  paths: PathEntry[]
  /** Feat / özellik havuzu. */
  features: FeatureEntry[]
}

export function emptyPack(name = 'Özel İçerik'): ContentPack {
  return {
    id: `pack-${Date.now().toString(36)}`,
    name,
    version: '1.0.0',
    author: '',
    createdAt: Date.now(),
    description: '',
    classes: [],
    races: [],
    backgrounds: [],
    items: [],
    spells: [],
    paths: [],
    features: [],
  }
}

// ---------------------------------------------------------------------------
// DOĞRULAMA
// ---------------------------------------------------------------------------

export interface ValidationResult {
  ok: boolean
  errors: string[]
  /** Hatalı alanların yolu → mesaj. Form bu haritayı kullanır. */
  fieldErrors: Record<string, string>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function checkShape(
  value: unknown,
  path: string,
  required: string[],
  errors: string[],
  fieldErrors: Record<string, string>,
): value is Record<string, unknown> {
  if (!isRecord(value)) {
    errors.push(`${path}: nesne olmalı.`)
    return false
  }
  let ok = true
  for (const key of required) {
    if (value[key] == null) {
      const message = `${path}.${key} eksik.`
      errors.push(message)
      fieldErrors[`${path}.${key}`] = message
      ok = false
    }
  }
  return ok
}

/**
 * Paketi doğrular. Katı ama ALICI: eksik alanları tek tek listeler ki form
 * kullanıcıya nerede hata olduğunu göstersin.
 */
export function validateContentPack(input: unknown): ValidationResult {
  const errors: string[] = []
  const fieldErrors: Record<string, string> = {}

  if (!checkShape(input, 'pack', ['name'], errors, fieldErrors)) {
    return { ok: false, errors, fieldErrors }
  }
  const raw = input as Record<string, unknown>

  if (typeof raw.name !== 'string' || raw.name.trim() === '') {
    const message = 'Paket adı gerekli.'
    errors.push(message)
    fieldErrors['pack.name'] = message
  }

  // Dizileri normalize et (yoksa boş dizi kabul et)
  const arrays = [
    'classes',
    'races',
    'backgrounds',
    'items',
    'spells',
    'paths',
    'features',
  ] as const
  for (const key of arrays) {
    if (raw[key] != null && !Array.isArray(raw[key])) {
      const message = `pack.${key} dizi olmalı.`
      errors.push(message)
      fieldErrors[`pack.${key}`] = message
    }
  }

  const classes = (raw.classes ?? []) as unknown[]
  classes.forEach((cls, i) => {
    if (!checkShape(cls, `classes[${i}]`, ['id', 'name', 'hitDie'], errors, fieldErrors)) return
    const c = cls as Record<string, unknown>
    if (typeof c.hitDie !== 'number' || c.hitDie < 2 || c.hitDie > 12) {
      const message = `classes[${i}].hitDie 2–12 arasında olmalı (örn. 10 = d10).`
      errors.push(message)
      fieldErrors[`classes[${i}].hitDie`] = message
    }
    if (typeof c.skillChoices !== 'number' || c.skillChoices < 0) {
      const message = `classes[${i}].skillChoices 0 veya pozitif olmalı.`
      errors.push(message)
      fieldErrors[`classes[${i}].skillChoices`] = message
    }
    if (!Array.isArray(c.skillChoicePool)) {
      const message = `classes[${i}].skillChoicePool dizi olmalı.`
      errors.push(message)
      fieldErrors[`classes[${i}].skillChoicePool`] = message
    }
  })

  const backgrounds = (raw.backgrounds ?? []) as unknown[]
  backgrounds.forEach((bg, i) => {
    if (!checkShape(bg, `backgrounds[${i}]`, ['id', 'name'], errors, fieldErrors)) return
    const b = bg as Record<string, unknown>
    if (!Array.isArray(b.skillProficiencies)) {
      const message = `backgrounds[${i}].skillProficiencies dizi olmalı.`
      errors.push(message)
      fieldErrors[`backgrounds[${i}].skillProficiencies`] = message
    }
  })

  const paths = (raw.paths ?? []) as unknown[]
  paths.forEach((path, i) => {
    if (!checkShape(path, `paths[${i}]`, ['id', 'name'], errors, fieldErrors)) return
    const p = path as Record<string, unknown>
    // Ozellikler dizi olmali; yolun kalbi budur.
    if (p.features != null && !Array.isArray(p.features)) {
      const message = `paths[${i}].features dizi olmalı.`
      errors.push(message)
      fieldErrors[`paths[${i}].features`] = message
    }
    // maxPaths yalnizca TAVAN. 0 veya negatif bir tavan anlamsizdir ve
    // yolun hic kullanimini engeller; bu bir icerik hatasi.
    if (p.maxPaths != null && (typeof p.maxPaths !== 'number' || p.maxPaths < 1)) {
      const message = `paths[${i}].maxPaths en az 1 olmalı (yok = sınırsız).`
      errors.push(message)
      fieldErrors[`paths[${i}].maxPaths`] = message
    }
  })

  // Kimlik çakışması: aynı id iki kez geçmemeli
  for (const key of arrays) {
    const seen = new Set<string>()
    for (const [i, entry] of (raw[key] as unknown[]).entries()) {
      const id = isRecord(entry) ? entry.id : undefined
      if (typeof id !== 'string') continue
      if (seen.has(id)) {
        const message = `${key}[${i}].id çakışıyor: "${id}".`
        errors.push(message)
        fieldErrors[`${key}[${i}].id`] = message
      }
      seen.add(id)
    }
  }

  return { ok: errors.length === 0, errors, fieldErrors }
}

// ---------------------------------------------------------------------------
// BİRLEŞTİRME
// ---------------------------------------------------------------------------

function toRecord<T extends { id: string }>(items: readonly T[]): Record<string, T> {
  const out: Record<string, T> = {}
  for (const item of items) out[item.id] = item
  return out
}

/**
 * Paketi temel içeriğin üstüne birleştirir.
 *
 * ÇAKIŞMA KURALI: Paket, temel içeriğin ÜSTÜNE yazar. Yani bir özel sınıf
 * `paladin` id'siyle gelirse SRD Paladin'inin yerini alır — bu bilinçli bir
 * karar: "SRD Paladin'i şöyle değiştirdim" demek isteyen kullanıcı da
 * ifade edebilsin.
 *
 * @returns Yeni bir `ContentIndex`. Girdiler DEĞİŞTİRİLMEZ.
 */
export function mergeContent(base: ContentIndex, pack: ContentPack): ContentIndex {
  return {
    classes: { ...base.classes, ...toRecord(pack.classes) },
    races: { ...base.races, ...toRecord(pack.races) },
    backgrounds: { ...base.backgrounds, ...toRecord(pack.backgrounds) },
    items: { ...base.items, ...toRecord(pack.items) },
    spells: { ...base.spells, ...toRecord(pack.spells) },
    features: { ...base.features, ...toRecord(pack.features) },
    // Ayni cakisma kurali yollar icin de gecerli: paket yolu ayni id ile
    // yollarsa temel yolun USTUNE yazar.
    paths: { ...base.paths, ...toRecord(pack.paths) },
  }
}

/** Birden çok paketi sırayla uygular (sonraki öncekinin üstüne yazar). */
export function mergePacks(base: ContentIndex, packs: readonly ContentPack[]): ContentIndex {
  return packs.reduce<ContentIndex>((acc, pack) => mergeContent(acc, pack), base)
}

// ---------------------------------------------------------------------------
// SINIF TABLOSU ÜRETİCİ
// ---------------------------------------------------------------------------

/**
 * 5e tam/yarı büyücü yuva tabloları. Özel sınıf sihirbazının 20 satır
 * elle yazmasını önler: tablo türetilir.
 */
const FULL_CASTER_SLOTS: Record<number, number> = {
  1: 2,
  2: 3,
  3: 3,
  4: 3,
  5: 4,
  6: 4,
  7: 4,
  8: 4,
  9: 5,
  10: 5,
  11: 5,
  12: 5,
  13: 6,
  14: 6,
  15: 6,
  16: 6,
  17: 6,
  18: 6,
  19: 6,
  20: 6,
}
const HALF_CASTER_SLOTS: Record<number, number> = {
  1: 2,
  2: 2,
  3: 3,
  4: 3,
  5: 4,
  6: 4,
  7: 5,
  8: 5,
  9: 6,
  10: 6,
  11: 6,
  12: 6,
  13: 6,
  14: 6,
  15: 6,
  16: 6,
  17: 6,
  18: 6,
  19: 6,
  20: 6,
}
const THIRD_CASTER_SLOTS: Record<number, number> = {
  1: 0,
  2: 0,
  3: 2,
  4: 3,
  5: 3,
  6: 3,
  7: 4,
  8: 4,
  9: 5,
  10: 5,
  11: 5,
  12: 5,
  13: 6,
  14: 6,
  15: 6,
  16: 6,
  17: 6,
  18: 6,
  19: 6,
  20: 6,
}

export type CasterKind = 'none' | 'full' | 'half' | 'third'

/** 5e yetenek geliştirme seviyeleri (varsayılan). */
export const DEFAULT_ASI_LEVELS = [4, 8, 12, 16, 19]

export interface ClassBlueprint {
  id: string
  name: string
  hitDie: number
  primaryAbility: AbilityKey
  savingThrowProficiencies: AbilityKey[]
  armorProficiencies: string[]
  weaponProficiencies: string[]
  skillChoicePool: string[]
  skillChoices: number
  casterKind: CasterKind
  spellAbility: AbilityKey
  spellPreparation: 'prepared' | 'known'
  asiLevels: number[]
  subclassLevel: number
  /** Kullanıcının eklediği düz özellik listesi. */
  features: { level: number; name: string; description: string }[]
}

export function emptyBlueprint(name = 'Yeni Sınıf'): ClassBlueprint {
  return {
    id: `snf-${Date.now().toString(36)}`,
    name,
    hitDie: 10,
    primaryAbility: 'str',
    savingThrowProficiencies: ['str', 'con'],
    armorProficiencies: ['light', 'medium'],
    weaponProficiencies: ['simple'],
    skillChoicePool: ['athletics', 'perception'],
    skillChoices: 2,
    casterKind: 'none',
    spellAbility: 'int',
    spellPreparation: 'prepared',
    asiLevels: [...DEFAULT_ASI_LEVELS],
    subclassLevel: 3,
    features: [{ level: 1, name: 'Sınıf Yeteneği', description: '' }],
  }
}

function slotTableFor(kind: CasterKind, level: number): Record<string, number> {
  if (kind === 'none') return {}
  const table =
    kind === 'full' ? FULL_CASTER_SLOTS : kind === 'half' ? HALF_CASTER_SLOTS : THIRD_CASTER_SLOTS
  const count = table[level] ?? 0
  return count > 0 ? { 1: count } : {}
}

function spellcastingFor(bp: ClassBlueprint): SpellcastingSpec {
  if (bp.casterKind === 'none') return { mode: 'none' }
  const cantripProgression =
    bp.casterKind === 'full'
      ? [
          { level: 1, count: 2 },
          { level: 4, count: 3 },
          { level: 10, count: 4 },
          { level: 17, count: 5 },
        ]
      : [{ level: 1, count: 2 }]

  if (bp.casterKind === 'half') {
    return {
      mode: 'half',
      ability: bp.spellAbility,
      preparation: bp.spellPreparation,
      cantripProgression,
    }
  }
  return {
    mode: 'full',
    ability: bp.spellAbility,
    preparation: bp.spellPreparation,
    cantripProgression,
  }
}

/**
 * Şablonu tam bir `ClassEntry`'ye çevirir: 20 seviyelik tablo, yuva
 * tabloları ve ASI seviyeleri TÜRETİLİR. Kullanıcı yalnızca özellik listesini
 * ve sayısal ayarları girer.
 */
export function blueprintToClass(bp: ClassBlueprint): ClassEntry {
  const asiSet = new Set(bp.asiLevels)
  const byLevel = new Map<number, FeatureEntry[]>()
  for (const f of bp.features) {
    const level = Math.max(1, Math.min(20, Math.round(f.level)))
    const entry: FeatureEntry = {
      id: `${bp.id}-l${level}-${f.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .slice(0, 24)}`,
      name: f.name || 'İsimsiz Özellik',
      description: f.description,
    }
    byLevel.set(level, [...(byLevel.get(level) ?? []), entry])
  }

  const levels: ClassLevelEntry[] = Array.from({ length: 20 }, (_, i) => i + 1).map((level) => ({
    level,
    features: byLevel.get(level) ?? [],
    spellSlots: slotTableFor(bp.casterKind, level),
    asi: asiSet.has(level) ? 1 : 0,
    subclassLevel: level === bp.subclassLevel ? bp.subclassLevel : undefined,
  }))

  return {
    id: bp.id,
    name: bp.name,
    hitDie: bp.hitDie,
    primaryAbility: bp.primaryAbility,
    savingThrowProficiencies: bp.savingThrowProficiencies,
    armorProficiencies: bp.armorProficiencies as ClassEntry['armorProficiencies'],
    weaponProficiencies: bp.weaponProficiencies,
    skillChoicePool: bp.skillChoicePool,
    skillChoices: bp.skillChoices,
    spellcasting: spellcastingFor(bp),
    levels,
  }
}

// ---------------------------------------------------------------------------
// ARKA PLAN ŞABLONU
// ---------------------------------------------------------------------------

export interface BackgroundBlueprint {
  id: string
  name: string
  description: string
  skillProficiencies: string[]
  toolProficiencies: string[]
  languages: string[]
  gold: number
  featureName: string
  featureDescription: string
}

export function emptyBackgroundBlueprint(name = 'Yeni Arka Plan'): BackgroundBlueprint {
  return {
    id: `ark-${Date.now().toString(36)}`,
    name,
    description: '',
    skillProficiencies: ['skill.athletics', 'skill.stealth'],
    toolProficiencies: [],
    languages: ['language.common'],
    gold: 10,
    featureName: 'Özellik',
    featureDescription: '',
  }
}

export function blueprintToBackground(bp: BackgroundBlueprint): BackgroundEntry {
  return {
    id: bp.id,
    name: bp.name,
    skillProficiencies: bp.skillProficiencies,
    toolProficiencies: bp.toolProficiencies,
    languages: bp.languages,
    gold: bp.gold,
    features: bp.featureName
      ? [
          {
            id: `${bp.id}-ozellik`,
            name: bp.featureName,
            description: bp.featureDescription,
          },
        ]
      : [],
  }
}

// ---------------------------------------------------------------------------
// YARDIMCILAR
// ---------------------------------------------------------------------------

/** Sınıf havuzunda seçilebilecek beceri listesi (etiket + anahtar). */
export const SKILL_POOL_OPTIONS: { key: string; nameTr: string }[] = SKILLS.map((s) => ({
  key: s.key,
  nameTr: s.labelTr,
}))

export const ABILITY_OPTIONS: { key: AbilityKey; nameTr: string }[] = ABILITY_KEYS.map((k) => ({
  key: k,
  nameTr: {
    str: 'Güç',
    dex: 'Çeviklik',
    con: 'Dayanıklılık',
    int: 'Zekâ',
    wis: 'Algı',
    cha: 'Karizma',
  }[k],
}))

export const ARMOR_OPTIONS = [
  { key: 'light', nameTr: 'Hafif' },
  { key: 'medium', nameTr: 'Orta' },
  { key: 'heavy', nameTr: 'Ağır' },
  { key: 'shields', nameTr: 'Kalkan' },
] as const

export const WEAPON_OPTIONS = [
  { key: 'simple', nameTr: 'Basit' },
  { key: 'martial', nameTr: 'Savaş (Martial)' },
] as const

/** Bir sınıfın kaç yetenek geliştirmesi olduğunu hesaplar. */
export function countAsi(classEntry: ClassEntry, level: number): number {
  let total = 0
  for (const tier of classEntry.levels) {
    if (tier.level > level) break
    total += tier.asi
  }
  return total
}

/** Paketteki tüm içerik kimliklerini döndürür (çakışma kontrolü / dışa aktarım). */
export function packIds(pack: ContentPack): Record<string, string[]> {
  return {
    classes: pack.classes.map((c) => c.id),
    races: pack.races.map((r) => r.id),
    backgrounds: pack.backgrounds.map((b) => b.id),
    items: pack.items.map((i) => i.id),
    spells: pack.spells.map((s) => s.id),
    paths: pack.paths.map((p) => p.id),
    features: pack.features.map((f) => f.id),
  }
}

/** Paketin kaç içerik kaydı taşıdığını döndürür. */
export function packSize(pack: ContentPack): number {
  return Object.values(packIds(pack)).reduce((sum, ids) => sum + ids.length, 0)
}
